import { Application, Assets, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import type { ThemeAssetEntry } from '../../theme';
import { loadUiFonts } from '../board/ui';
import { AccountScreen, type AccountPress, type AccountUi, type AccountView, type FieldRect } from './AccountScreen';
import { destroyPixiApp } from '../../core/destroyPixiApp';

export interface AccountArtUrls {
  /** The board's floor (the first slot's reel column). */
  floor: string;
  envelope: string;
  tickOff: string;
  tickOn: string;
}

export interface MountAccountDialogOptions {
  art: AccountArtUrls;
  /** The painted button plates. */
  plates?: Record<string, ThemeAssetEntry>;
  view: AccountView;
  onPress: (press: AccountPress) => void;
  /** Where the email field is (null: none), so the host can put its input
   *  there. */
  onField: (rect: FieldRect | null) => void;
}

export interface AccountDialogHandle {
  show: (view: AccountView) => void;
  destroy: () => void;
}

declare global {
  interface Window {
    /** The account board on screen, as plain data, for browser tests. */
    __accountDialog?: {
      ui: () => AccountUi;
      where: (label: string) => { x: number; y: number } | null;
    };
  }
}

async function load(url: string): Promise<Texture | null> {
  try {
    return await Assets.load<Texture>(url);
  } catch {
    return null;
  }
}

/**
 * Mount the account board into `el` on its own transparent Pixi canvas.
 * Keeps the board above a phone's keyboard (the visual viewport) while the
 * email field is in use.
 */
export function mountAccountDialog(el: HTMLElement, options: MountAccountDialogOptions): AccountDialogHandle {
  const app = new Application();
  let gone = false;
  let screen: AccountScreen | null = null;
  let pending: AccountView = options.view;
  const fit = (): void => {
    if (!screen) return;
    const vv = window.visualViewport;
    const visible = vv ? { top: vv.offsetTop, height: Math.min(vv.height, app.screen.height) } : undefined;
    screen.layout(app.screen.width, app.screen.height, visible);
  };
  // Keys go to the email field, or stop here: the slot underneath listens
  // on the window, and a space or an "i" mustn't spin it or open its
  // paytable behind the board. (The host's field stops its own keys.)
  const onKey = (event: KeyboardEvent): void => {
    if (event.target instanceof HTMLInputElement) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    event.stopPropagation();
    if (event.key === 'Escape') options.onPress('cancel');
  };
  const ready = (async () => {
    await app.init({
      resizeTo: el,
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio,
      autoDensity: true,
    });
    if (gone) return;
    el.appendChild(app.canvas);
    const [floor, envelope, tickOff, tickOn] = await Promise.all([
      load(options.art.floor),
      load(options.art.envelope),
      load(options.art.tickOff),
      load(options.art.tickOn),
      loadUiFonts(),
      options.plates ? AssetLoader.shared.loadAssets(options.plates).catch(() => undefined) : undefined,
    ]);
    if (gone) return;
    screen = new AccountScreen({ floor, envelope, tickOff, tickOn }, options.onPress, options.onField);
    screen.show(pending);
    app.stage.addChild(screen);
    fit();
    void screen.open();
    app.renderer.on('resize', fit);
    window.visualViewport?.addEventListener('resize', fit);
    window.visualViewport?.addEventListener('scroll', fit);
    window.addEventListener('keydown', onKey, true);
    window.__accountDialog = { ui: () => screen!.ui(), where: (label) => screen!.where(label) };
  })();
  return {
    show: (view) => {
      pending = view;
      screen?.show(view);
    },
    destroy: () => {
      gone = true;
      window.visualViewport?.removeEventListener('resize', fit);
      window.visualViewport?.removeEventListener('scroll', fit);
      window.removeEventListener('keydown', onKey, true);
      delete window.__accountDialog;
      options.onField(null);
      // The floor and plates are the slot's too: leave them loaded. Only this
      // canvas goes, leaving what the slot's renderer shares alone
      // (core/destroyPixiApp).
      void ready.finally(() => destroyPixiApp(app, 'AccountBoard'));
    },
  };
}
